<script setup>
// X132 (Grant: "it didn't even ask me about all the challenge rules"): a server-built settings card: every rule the
// competition's form has (question.settings), pre-filled with the form's defaults and editable inline, one Confirm. Rows
// are the server's (key, words, kind, options); a dependent row shows only while its parent row holds the named value; a
// money row is typed in the club's currency and sent in minor units. Confirm sends every row's value by key.
import {computed, ref, watch} from 'vue';
import {settingsValuesFor} from '~/utils/setupAssistant';

const props = defineProps({
  question: {type: Object, required: true},
  answered: {type: Boolean, default: false},
  disabled: {type: Boolean, default: false},
});
const emit = defineEmits(['confirm']);

const rows = computed(() => (props.question?.settings ?? []).filter(r => r && r.key));
const initial = () => Object.fromEntries(rows.value.map(r => [r.key, r.type === 'money' ? (r.value == null ? '' : String(Number(r.value) / 100)) : (r.value ?? (r.type === 'bool' ? false : ''))]));
const values = ref(initial());
watch(() => props.question?.id, () => { values.value = initial(); });

const shown = (r) => !r.dependsOn || String(values.value[r.dependsOn] ?? '') === String(r.dependsValue ?? '');
const selectItems = (r) => (r.options ?? []).map(o => ({value: o.handle, title: o.label}));
const inputId = (r) => `${props.question.id}-${r.key}`;
const confirm = () => {
  if (props.answered || props.disabled) return;
  emit('confirm', {question: props.question, values: settingsValuesFor(rows.value, values.value)});
};
</script>

<template>
  <div class="ssc" :class="{'ssc--answered': answered}">
    <div v-for="r in rows" :key="r.key" class="ssc-row" :class="{'ssc-row--off': !shown(r)}">
      <template v-if="shown(r)">
        <template v-if="r.type === 'bool'">
          <v-checkbox
              v-model="values[r.key]"
              :label="r.label"
              :hint="r.help || undefined"
              :persistent-hint="!!r.help"
              :disabled="answered || disabled"
              density="compact"
              hide-details="auto"
              class="ssc-bool"
          />
        </template>
        <template v-else>
          <label class="tl-label mb-1" :for="inputId(r)">{{ r.label }}</label>
          <v-select
              v-if="r.type === 'select'"
              :id="inputId(r)"
              v-model="values[r.key]"
              :items="selectItems(r)"
              :hint="r.help || undefined"
              :persistent-hint="!!r.help"
              :disabled="answered || disabled"
              density="compact"
              hide-details="auto"
          />
          <v-text-field
              v-else
              :id="inputId(r)"
              v-model="values[r.key]"
              type="number"
              :step="r.type === 'money' ? '0.01' : '1'"
              :min="r.type === 'money' ? 0 : r.min ?? undefined"
              :max="r.max ?? undefined"
              :prefix="r.type === 'money' ? '£' : undefined"
              :placeholder="r.optional ? (r.type === 'money' ? 'Free' : 'Not set') : undefined"
              :hint="r.help || undefined"
              :persistent-hint="!!r.help"
              :disabled="answered || disabled"
              density="compact"
              hide-details="auto"
          />
        </template>
      </template>
    </div>
    <div v-if="!answered" class="ssc-confirm">
      <v-btn size="small" color="primary" variant="flat" :disabled="disabled" @click="confirm">Confirm</v-btn>
      <span class="ssc-hint">Change anything first; it all goes on the draft.</span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ssc {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
}

.ssc-row {
  min-width: 0;

  &--off {
    display: none;
  }
}

.ssc-bool {
  margin-left: -8px;

  /* X138 (R68 L6): a checkbox's hint ("Off: they go straight to the bottom of the ladder") wraps with room between its lines */
  :deep(.v-input__details) { padding-top: 2px; padding-left: 8px; }
  :deep(.v-messages) { min-height: 0; }
  :deep(.v-messages__message) { line-height: 1.45; }
}

.ssc-confirm {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 2px;
}

.ssc-hint {
  font-size: 12px;
  color: rgb(var(--v-theme-textSubtle));
}
</style>
